import { useEffect, useState } from "react";
import { Bell, BellOff, Volume2, Vibrate, Clock, MoonStar, Smartphone, Info, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { uiSoundsEnabled, setUiSounds, playSuccess } from "@/lib/sounds";
import { enablePush, disablePush, getSubscription, permissionState, pushSupported, isIOS, isStandalone, LEAD_OPTIONS } from "@/lib/push";

function Row({ icon: Icon, title, desc, children, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5" data-testid={testId}>
      <Icon size={18} style={{ color: "#0A6373" }} className="shrink-0" />
      <div className="min-w-0 flex-1"><div className="text-sm font-medium">{title}</div>{desc && <div className="ga-muted text-xs">{desc}</div>}</div>
      {children}
    </div>
  );
}

export default function NotificationSettings() {
  const { user, updateUser } = useAuth();
  const s = user?.notification_settings || {};
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(permissionState());
  const [busy, setBusy] = useState(false);
  const [uiSounds, setUiSoundsState] = useState(uiSoundsEnabled);
  const isCustom = ![0, 5, 15, 30, 60, 1440].includes(s.lead_minutes);
  const [customLead, setCustomLead] = useState(isCustom ? s.lead_minutes : 120);
  const [useCustom, setUseCustom] = useState(isCustom);

  useEffect(() => { getSubscription().then((sub) => setSubscribed(!!sub)); }, []);

  const save = async (patch) => {
    try { await updateUser({ notification_settings: { ...patch, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } }); } catch (e) { toast.error(errorMessage(e)); }
  };

  const toggleEnabled = async (on) => {
    setBusy(true);
    try {
      if (on) {
        await enablePush();
        setSubscribed(true);
        await save({ enabled: true });
        toast.success("Notificações ativadas neste aparelho.");
      } else {
        await disablePush();
        setSubscribed(false);
        await save({ enabled: false });
        toast.success("Notificações desativadas.");
      }
    } catch (e) {
      if (e.code === "denied") toast.error("Permissão negada. Veja como ativar abaixo.");
      else toast.error(e.message || "Não foi possível ativar as notificações.");
    } finally {
      setPermission(permissionState());
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success("Enviamos uma notificação de teste.");
      else toast.error("Nenhum aparelho registrado. Ative as notificações primeiro.");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const active = s.enabled !== false && subscribed && permission === "granted";

  return (
    <div className="pb-10">
      <PageHeader title="Sons e notificações" />
      <div className="ga-fade-up space-y-3 px-5">
        <div className="ga-card divide-y overflow-hidden" style={{ borderColor: "var(--ga-border)" }}>
          <Row icon={active ? Bell : BellOff} title="Notificações" desc={active ? "Você será avisado na data e hora dos lembretes, mesmo com o app fechado." : "Ative para receber os lembretes no aparelho."} testId="notif-enabled-row">
            <Switch checked={active} disabled={busy || !pushSupported()} onCheckedChange={toggleEnabled} aria-label="Ativar notificações" data-testid="notif-enabled-switch" />
          </Row>
          <Row icon={Volume2} title="Som" desc="Tocar o som padrão do aparelho." testId="notif-sound-row">
            <Switch checked={s.sound !== false} onCheckedChange={(v) => save({ sound: v })} aria-label="Ativar som" data-testid="notif-sound-switch" />
          </Row>
          <Row icon={Vibrate} title="Vibração" desc="Quando o aparelho permitir." testId="notif-vibration-row">
            <Switch checked={s.vibration !== false} onCheckedChange={(v) => save({ vibration: v })} aria-label="Ativar vibração" data-testid="notif-vibration-switch" />
          </Row>
        </div>

        <div className="ga-card overflow-hidden" data-testid="ui-sounds-card">
          <Row icon={MousePointerClick} title="Sons da interface" desc="Toques curtos e discretos ao usar botões, menus e confirmações. Não altera o som das notificações." testId="ui-sounds-row">
            <Switch checked={uiSounds} onCheckedChange={(v) => { setUiSounds(v); setUiSoundsState(v); if (v) setTimeout(playSuccess, 30); }} aria-label="Sons da interface" data-testid="ui-sounds-switch" />
          </Row>
        </div>

        {!pushSupported() && <div className="ga-card p-4 text-sm" data-testid="notif-unsupported"><Info size={16} className="mb-1" style={{ color: "#0A6373" }} /> Este navegador não suporta notificações push. Use o Chrome, Edge, Firefox ou Safari (iOS 16.4+ com o app instalado).</div>}
        {permission === "denied" && (
          <div className="ga-card p-4 text-sm" data-testid="notif-denied-help">
            <p className="font-semibold">As notificações estão bloqueadas neste navegador.</p>
            <p className="ga-muted mt-1 text-xs leading-relaxed">Para ativar: toque no ícone de cadeado ou nos três pontos ao lado do endereço → Permissões → Notificações → Permitir. No iPhone: Ajustes → Notificações → Clicou, Guardou!. Depois volte aqui e ative novamente.</p>
          </div>
        )}
        {isIOS() && !isStandalone() && (
          <div className="ga-card flex gap-3 p-4 text-sm" data-testid="notif-ios-hint"><Smartphone size={18} className="shrink-0" style={{ color: "#0A6373" }} /><p className="ga-muted text-xs leading-relaxed">No iPhone/iPad, as notificações só funcionam com o Clicou, Guardou! instalado: Compartilhar → Adicionar à Tela de Início, e abra pelo ícone.</p></div>
        )}

        <div className="ga-card p-4" data-testid="notif-lead-card">
          <div className="flex items-center gap-3"><Clock size={18} style={{ color: "#0A6373" }} /><div className="text-sm font-medium">Antecedência padrão</div></div>
          <select value={useCustom ? "custom" : s.lead_minutes ?? 0} onChange={(e) => { if (e.target.value === "custom") { setUseCustom(true); save({ lead_minutes: Number(customLead) }); } else { setUseCustom(false); save({ lead_minutes: Number(e.target.value) }); } }} className="ga-input mt-3 py-2.5" data-testid="notif-lead-select">
            {LEAD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {useCustom && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <input type="number" min={1} value={customLead} onChange={(e) => setCustomLead(e.target.value)} onBlur={() => save({ lead_minutes: Number(customLead) || 0 })} className="ga-input w-28 py-2" data-testid="notif-lead-custom-input" />
              <span className="ga-muted text-xs">minutos antes</span>
            </div>
          )}
          <p className="ga-muted mt-2 text-xs">Você pode ajustar a antecedência de cada lembrete individualmente.</p>
        </div>

        <div className="ga-card p-4" data-testid="notif-quiet-card">
          <div className="flex items-center gap-3">
            <MoonStar size={18} style={{ color: "#0A6373" }} />
            <div className="flex-1"><div className="text-sm font-medium">Horário silencioso</div><div className="ga-muted text-xs">Lembretes desse período chegam quando ele termina.</div></div>
            <Switch checked={!!s.quiet_enabled} onCheckedChange={(v) => save({ quiet_enabled: v })} aria-label="Ativar horário silencioso" data-testid="notif-quiet-switch" />
          </div>
          {s.quiet_enabled && (
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <label>Início<input type="time" value={s.quiet_start || "22:00"} onChange={(e) => save({ quiet_start: e.target.value })} className="ga-input mt-1 py-2" data-testid="notif-quiet-start" /></label>
              <label>Fim<input type="time" value={s.quiet_end || "07:00"} onChange={(e) => save({ quiet_end: e.target.value })} className="ga-input mt-1 py-2" data-testid="notif-quiet-end" /></label>
            </div>
          )}
        </div>

        <button onClick={sendTest} className="ga-btn-ghost w-full" data-testid="notif-test-button">Enviar notificação de teste</button>

        <div className="ga-card flex gap-3 p-4" data-testid="notif-limitations">
          <Info size={18} className="shrink-0" style={{ color: "#0A6373" }} />
          <p className="ga-muted text-xs leading-relaxed">Som e vibração seguem as regras do seu aparelho: se ele estiver no silencioso ou com "Não perturbe", a notificação chega sem som. Alguns navegadores (especialmente no iPhone) não permitem som personalizado em segundo plano — usamos o som padrão do sistema.</p>
        </div>
      </div>
    </div>
  );
}
